/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { get } from '../../../services/GenesisApi';
import {
  GET_NOMINATION_COUNTS,
  GET_NOMINATIONS,
} from '../../../constants/Nominations';
import {
  INomination,
  IExcludedForecasts,
} from '../../../types/Nomination.type';
import { sendNotification } from '../../../UI/Notification';
import { COMMON_MESSAGES, UK_TIME_ZONE } from '../../../constants/Common';
import {
  handleError,
  getCurrentGasDay,
  getIsWithinDay,
  getIsDayAhead,
} from '../../../utils/common/utils';
import { replaceStartTime } from '../../../utils/nominations';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface INominationState {
  rejectedNominations: number;
  loading: boolean;
  gasDay: string;
  entryNominations: INomination[];
  exitNominations: INomination[];
  excludedForecastDtos: IExcludedForecasts[];
  lastRefreshedDateTime: string;
}

interface INominationPayload {
  entryNominations: INomination[];
  exitNominations: INomination[];
  excludedForecastDtos: IExcludedForecasts[];
  lastRefreshedDateTime: string;
}

export const initialState: INominationState = {
  rejectedNominations: 0,
  loading: false,
  gasDay: '',
  entryNominations: [],
  exitNominations: [],
  excludedForecastDtos: [],
  lastRefreshedDateTime: '',
};

export const getNominations = createAsyncThunk(
  'nominations/getNominations',
  async (gasDay: string) => {
    return get(`${GET_NOMINATIONS}?gasDay=${gasDay}`, {})
      .then((response) => {
        const currentGasDay = getCurrentGasDay();
        const ukTime = dayjs().tz(UK_TIME_ZONE);
        const targetDate = dayjs.tz(gasDay, UK_TIME_ZONE);
        const isWithinDay = getIsWithinDay(gasDay);
        const isDayAhead = getIsDayAhead(gasDay);
        // start time should editable for day ahead after 2:30PM of current gas day
        const targetTime = currentGasDay
          .set('hour', 14)
          .set('minute', 30)
          .set('second', 0)
          .set('millisecond', 0);

        const adjustedCurrentGasDay =
          ukTime.day() > currentGasDay.day()
            ? currentGasDay.add(1, 'day')
            : currentGasDay;
        const isStartTimeEditable =
          isWithinDay ||
          (isDayAhead && adjustedCurrentGasDay.isAfter(targetTime));

        const nominationsResponse = (nominationsArray: INomination[]) =>
          nominationsArray.map((nomination, index) => {
            const isPending =
              nomination.forecastETRM !== nomination.requestedNomination;
            const isReadonly =
              targetDate.isBefore(currentGasDay, 'day') ||
              nomination.isReadonly;
            const checked = !nomination.isReadonly ? isPending : false;
            let start = nomination.startTime;
            if (checked && isWithinDay) {
              start = ukTime.add(1, 'hour').hour() + 1;
              if (nomination.activityId.toLowerCase() === 'ip') {
                start += 1; // for IP Nomination
              }
            }

            return {
              ...nomination,
              id: index,
              start: replaceStartTime(start),
              forecast: nomination.forecastETRM?.toLocaleString('en-US'),
              isReadonly,
              isPending,
              checked,
              isStartTimeEditable,
            };
          });

        const entryNominations = nominationsResponse(
          response.data?.entryNomination ?? []
        );
        const exitNominations = nominationsResponse(
          response.data?.exitNomination ?? []
        );
        const excludedForecastDtos = response.data?.excludedForecastDtos ?? [];

        return {
          entryNominations,
          exitNominations,
          excludedForecastDtos,
          lastRefreshedDateTime: response.data?.lastRefreshedDateTime ?? '',
        };
      })
      .catch((error) => {
        sendNotification({
          type: 'error',
          message: COMMON_MESSAGES.ERROR,
        });
        throw error;
      });
  }
);

export const getNominationCount = createAsyncThunk(
  'nominations/getNominationCount',
  async () => {
    const gasDay = getCurrentGasDay().format(COMMON_MESSAGES.DATE_YYYY_MM_DD);
    return get(`${GET_NOMINATION_COUNTS}?gasDay=${gasDay}`)
      .then((response) => {
        return response.data?.rejectedNominationCount ?? 0;
      })
      .catch((error) => {
        handleError(error, COMMON_MESSAGES.ERROR);

        throw error;
      });
  }
);

const nominations = createSlice({
  name: 'nominationActions',
  initialState,
  reducers: {
    updateGasDay: (
      state: INominationState,
      { payload }: PayloadAction<{ gasDay: string }>
    ) => {
      state.gasDay = payload.gasDay;
    },
    setRejectedNominationsCount: (
      state: INominationState,
      { payload }: PayloadAction<{ count: number }>
    ) => {
      state.rejectedNominations = payload.count;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getNominations.pending, (state: INominationState) => {
        state.loading = true;
        state.entryNominations = [];
        state.exitNominations = [];
        state.lastRefreshedDateTime = '';
        state.excludedForecastDtos = [];
      })
      .addCase(
        getNominations.fulfilled,
        (
          state: INominationState,
          { payload }: PayloadAction<INominationPayload>
        ) => {
          state.loading = false;
          state.entryNominations = payload.entryNominations || [];
          state.exitNominations = payload.exitNominations || [];
          state.lastRefreshedDateTime = payload.lastRefreshedDateTime;
          state.excludedForecastDtos = payload.excludedForecastDtos || [];
        }
      )
      .addCase(getNominations.rejected, (state: INominationState) => {
        state.loading = false;
        state.entryNominations = [];
        state.exitNominations = [];
        state.lastRefreshedDateTime = '';
        state.excludedForecastDtos = [];
      })
      .addCase(
        getNominationCount.fulfilled,
        (state: INominationState, { payload }: PayloadAction<number>) => {
          state.rejectedNominations = payload;
        }
      )
      .addCase(getNominationCount.rejected, (state: INominationState) => {
        state.rejectedNominations = 0;
      });
  },
});

export const nominationsActions = nominations.actions;
export default nominations.reducer;
